<template>
  <div class="configurator orders-list">
    <div class="configurator-actions">
      <div>
        <div class="configurator-title">
          {{ $t('configurator.list.title') }}
        </div>
        <div class="configurator-subtitle">
          {{ $t('configurator.list.subtitle') }}
        </div>
      </div>
      <b-button
        v-if="canCreate"
        variant="primary"
        class="cfg-btn"
        @click="newOrder"
      >
        <feather-icon
          icon="PlusIcon"
          size="14"
        />
        {{ $t('configurator.new_offer') }}
      </b-button>
    </div>

    <div class="cfg-card">
      <div class="cfg-card-header">
        <span class="cfg-card-title">{{ $t('configurator.list.offers') }}</span>
        <span class="cfg-badge cfg-badge-gray">{{ $tc('configurator.list.count', totalItems, { count: totalItems }) }}</span>
      </div>

      <div class="filters">
        <div class="filter">
          <div class="cfg-label">
            {{ $t('configurator.list.number') }}
          </div>
          <div class="search-wrap">
            <feather-icon
              icon="SearchIcon"
              class="search-icon"
            />
            <b-form-input
              v-model="number"
              type="text"
              :placeholder="$t('configurator.list.number_placeholder')"
            />
          </div>
        </div>
        <div
          v-if="currentUser.roles[0].name === 'super_admin'"
          class="filter"
        >
          <div class="cfg-label">
            {{ $t('configurator.list.client') }}
          </div>
          <SelectClients v-model="client" />
        </div>
        <div class="filter">
          <div class="cfg-label">
            {{ $t('configurator.list.status') }}
          </div>
          <v-select
            v-model="status"
            label="name"
            :filterable="false"
            :searchable="false"
            :options="selectStatus"
            :placeholder="$t('configurator.list.all_statuses')"
          />
        </div>
      </div>

      <b-table
        responsive
        :items="items"
        :fields="seleccionarcolumnas"
        :no-local-sorting="true"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :show-empty="true"
        :empty-text="$t('configurator.list.empty')"
        class="orders-table mb-0"
      >
        <template #cell(id)="data">
          <b-link
            :to="{ name: 'viewOrders', params: { id: data.item.id } }"
            class="cfg-badge cfg-badge-primary font-weight-bolder"
          >
            {{ numberFormat(data.item) }}
          </b-link>
        </template>
        <template #cell(created_at)="data">
          {{ dateColumn(data.item.created_at) }}
        </template>
        <template #cell(updated_at)="data">
          {{ dateColumn(data.item.updated_at) }}
        </template>
        <template #cell(amount)="data">
          <span class="amount">{{ priceFormatted(data.item.amount) }} €</span>
        </template>
        <template
          v-if="currentUser.roles[0].name === 'super_admin'"
          #cell(client)="data"
        >
          <b-link
            v-if="data.item.client"
            :to="{ name: 'viewClient', params: { id: data.item.client.id } }"
            class="client-link"
          >
            {{ data.item.client.name }}
          </b-link>
        </template>
        <template #cell(status)="data">
          <span
            class="cfg-badge dot"
            :class="statusClass(data.item.status.type)"
          >{{ $t(`configurator.order_status.${data.item.status.type}`) }}</span>
          <div class="cfg-hint mt-25">
            {{ dateColumn(data.item.status.created) }}
          </div>
        </template>
        <template #cell(budget)="data">
          <div class="doc-links">
            <a
              v-if="data.item.budget"
              :href="data.item.budget.path"
              target="_blank"
              class="doc-link"
            >
              <feather-icon
                icon="FileTextIcon"
                size="13"
              />
              {{ $t('configurator.list.quote') }}
            </a>
            <a
              v-if="data.item.delivery_note"
              :href="data.item.delivery_note.path"
              target="_blank"
              class="doc-link"
            >
              <feather-icon
                icon="TruckIcon"
                size="13"
              />
              {{ $t('configurator.list.delivery_note') }}
            </a>
            <a
              v-if="data.item.invoice"
              :href="data.item.invoice.path"
              target="_blank"
              class="doc-link"
            >
              <feather-icon
                icon="FileIcon"
                size="13"
              />
              {{ $t('configurator.list.invoice') }}
            </a>
            <span
              v-if="!data.item.budget && !data.item.delivery_note && !data.item.invoice"
              class="cfg-hint"
            >—</span>
          </div>
        </template>
        <template #cell(actions)="data">
          <div class="d-flex justify-content-end">
            <b-button
              v-b-tooltip.hover
              variant="outline-secondary"
              class="btn-icon mr-50"
              :title="$t('configurator.list.view')"
              :to="{ name: 'viewOrders', params: { id: data.item.id } }"
            >
              <feather-icon
                icon="EyeIcon"
                size="14"
              />
            </b-button>
            <b-button
              v-if="data.item.status.type === 'pending'"
              v-b-tooltip.hover
              variant="outline-secondary"
              class="btn-icon mr-50"
              :title="$t('configurator.list.edit')"
              @click="goToOrder(data.item.id)"
            >
              <feather-icon
                icon="EditIcon"
                size="14"
              />
            </b-button>
            <b-button
              v-if="deletePermission(data.item)"
              v-b-tooltip.hover
              variant="outline-danger"
              class="btn-icon"
              :title="$t('configurator.remove')"
              @click="deleteOrder(data.item)"
            >
              <feather-icon
                icon="TrashIcon"
                size="14"
              />
            </b-button>
          </div>
        </template>
      </b-table>

      <div
        v-if="totalItems > pageLength"
        class="pagination-bar"
      >
        <div class="d-flex align-items-center">
          <span class="cfg-hint mr-50">{{ $t('configurator.list.per_page') }}</span>
          <b-form-select
            v-model="pageLength"
            :options="pages"
            size="sm"
            class="per-page"
            @input="handlePageChange"
          />
        </div>
        <b-pagination
          v-model="currentPage"
          :total-rows="totalItems"
          :per-page="pageLength"
          first-number
          last-number
          align="right"
          prev-class="prev-item"
          next-class="next-item"
          class="mb-0"
          @change="handleChangePage"
        >
          <template #prev-text>
            <feather-icon
              icon="ChevronLeftIcon"
              size="16"
            />
          </template>
          <template #next-text>
            <feather-icon
              icon="ChevronRightIcon"
              size="16"
            />
          </template>
        </b-pagination>
      </div>
    </div>

    <b-modal
      v-model="modalShow"
      :hide-footer="true"
    >
      <h4 class="text-center">
        {{ $t('orders.modal_mobile_text') }}
      </h4>
      <h5 class="text-center">
        {{ $t('orders.modal_mobile_warning') }}
      </h5>
      <div class="text-center mb-1">
        <b-link
          :to="{ name: 'products' }"
          class="btn btn-primary m-1"
        >
          OK
        </b-link>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  BPagination,
  BFormInput,
  BFormSelect,
  BButton,
  BTable,
  BLink,
  BModal,
  VBTooltip,
} from 'bootstrap-vue'
import 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import { config } from '@/shared/app.config'
import { TimestampToFlatPickr, TimestampToFlatPickrOnlyYear } from '@/libs/helpers'
import vSelect from 'vue-select'
import { clearDraft } from '@/store/configurator'
import SelectClients from '@/components/clients/select/ClientsSelect.vue'

export default {
  components: {
    BButton,
    BTable,
    BPagination,
    BFormInput,
    BLink,
    BFormSelect,
    BModal,
    vSelect,
    SelectClients,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      log: [],
      sortBy: '',
      sortDesc: '',
      currentPage: 1,
      pageLength: 10,
      dir: false,
      statusOrderVariant: config.statusOrderVariants,
      webApiBase: config.webApiBase,
      pages: ['10', '15', '25'],
      columns_admin: [
        {
          label: this.$t('configurator.list.number'),
          key: 'id',
          sortable: true,
        },
        {
          label: this.$t('configurator.list.date'),
          key: 'created_at',
          sortable: true,
        },
        {
          label: this.$t('configurator.list.updated_at'),
          key: 'updated_at',
          sortable: true,
        },
        {
          label: this.$t('configurator.list.num_products'),
          key: 'num_products',
          sortable: true,
        },
        {
          label: this.$t('configurator.list.amount'),
          key: 'amount',
        },
        {
          label: this.$t('configurator.list.status'),
          key: 'status',
        },
        {
          label: this.$t('configurator.list.client'),
          key: 'client',
        },
        {
          label: this.$t('configurator.list.documents'),
          key: 'budget',
        },
        {
          label: this.$t('configurator.list.actions'),
          key: 'actions',
          class: 'column-actions',
        },
      ],
      columns: [
        {
          label: this.$t('configurator.list.number'),
          key: 'id',
          sortable: true,
        },
        {
          label: this.$t('configurator.list.date'),
          key: 'created_at',
          sortable: true,
        },
        {
          label: this.$t('configurator.list.updated_at'),
          key: 'updated_at',
          sortable: true,
        },
        {
          label: this.$t('configurator.list.num_products'),
          key: 'num_products',
          sortable: true,
        },
        {
          label: this.$t('configurator.list.amount'),
          key: 'amount',
        },
        {
          label: this.$t('configurator.list.status'),
          key: 'status',
        },
        {
          label: this.$t('configurator.list.documents'),
          key: 'budget',
        },
        {
          label: this.$t('configurator.list.actions'),
          key: 'actions',
          class: 'column-actions',
        },
      ],
      searchTerm: '',
      status: '',
      client: '',
      number: '',
      modalShow: false,
    }
  },
  computed: {
    ...mapGetters({
      items: 'orders/getItems',
      totalItems: 'orders/getTotalItems',
      currentUser: 'auth/getUser',
      selectStatus: 'orders/getSelectStatus',
    }),
    canCreate() {
      return ['admin', 'user'].includes(this.currentUser.roles[0].name)
    },
    seleccionarcolumnas() {
      if (this.currentUser.roles[0].name === 'super_admin') {
        return this.columns_admin
      }
      return this.columns
    },
  },
  watch: {
    sortBy() {
      this.currentPage = 1
      this.chargeData()
    },
    sortDesc() {
      this.currentPage = 1
      this.chargeData()
    },
    user() {
      this.currentPage = 1
      this.chargeData()
    },
    client() {
      this.currentPage = 1
      this.chargeData()
    },
    project() {
      this.currentPage = 1
      this.chargeData()
    },
    date_fin() {
      this.currentPage = 1
      this.chargeData()
    },
    date_ini() {
      this.currentPage = 1
      this.chargeData()
    },
    orderby() {
      this.currentPage = 1
      this.chargeData()
    },
    status() {
      this.currentPage = 1
      this.chargeData()
    },
    // Busca mientras se escribe, sin lanzar una petición por tecla
    number() {
      clearTimeout(this.numberTimer)
      this.numberTimer = setTimeout(() => {
        this.currentPage = 1
        this.chargeData()
      }, 400)
    },
  },
  async created() {
    console.log(window.screen.width)
    if (window.screen.width <= 768) {
      this.modalShow = true
      console.log(true)
    } else {
      await this.chargeData()
      await this.getSelectStatus({
        type: 'order',
      })
    }
  },
  methods: {
    ...mapActions({
      list: 'orders/getListOrders',
      delete: 'orders/delete',
      getSelectStatus: 'orders/getSelectStatus',
      reorder: 'products/setOrder',
    }),
    newOrder() {
      localStorage.removeItem('order')
      clearDraft()
      this.$router.push({ name: 'configurator' })
    },
    // Colores de estado del configurador
    statusClass(type) {
      if (type === 'pending') return 'cfg-badge-orange'
      if (type === 'revision') return 'cfg-badge-primary'
      if (['delivered', 'cast', 'send', 'ready'].includes(type)) return 'cfg-badge-green'
      return 'cfg-badge-gray'
    },
    goToOrder(id) {
      this.$router.push({ name: 'editConfigurator', params: { id } })
    },
    chargeData() {
      let orderBy = ''

      if (this.sortBy) {
        const type = this.sortDesc ? 'desc' : 'asc'
        orderBy = {
          column: this.sortBy,
          type,
        }
      }
      const userId = this.currentUser.id

      const status = this.status ? this.status.id : ''
      const client = this.client ? this.client.id : ''

      this.list({
        page: this.currentPage,
        per_page: this.pageLength,
        search: this.searchTerm,
        user_id: '',
        status_id: status,
        client_id: client,
        orderBy,
        number: this.number.trim(),
      })
    },
    handleSearch() {
      this.currentPage = 1
      this.chargeData()
    },
    handleChangePage(page) {
      this.currentPage = page
      this.chargeData()
    },
    handlePageChange() {
      this.currentPage = 1
      this.chargeData()
    },
    onSortChange(params) {
      this.currentPage = 1
      this.chargeData()
    },
    dateColumn(time) {
      return TimestampToFlatPickr(time)
    },
    dateColumnYear(time) {
      return TimestampToFlatPickrOnlyYear(time)
    },
    deleteOrder(item) {
      this.$bvModal
        .msgBoxConfirm(this.$t('configurator.list.delete_confirm', { order: this.numberFormat(item) }), {
          okVariant: 'danger',
          okTitle: this.$t('configurator.remove'),
          cancelTitle: this.$t('configurator.cancel'),
          cancelVariant: 'outline-secondary',
          footerClass: 'cfg-modal-footer',
          centered: true,
        })
        .then(value => {
          if (value === true) {
            this.delete(item.id)
          }
        })
    },
    priceFormatted(price) {
      const val = (price / 1).toFixed(2).replace('.', ',')
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    numberFormat({ id, created_at }) {
      return `${this.dateColumnYear(created_at)}/T${id}`
    },
    deletePermission(order) {
      if (this.currentUser.roles[0].name === 'super_admin') {
        return true
      } if (['pending', 'budget', 'delivered'].includes(order.status.type) && ['user', 'admin'].includes(this.currentUser.roles[0].name)) {
        return true
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import '@/views/configurator/configurator.scss';
</style>

<style lang="scss" scoped>
.search-wrap {
  position: relative;
  .form-control {
    padding-left: 2.25rem;
  }
  .search-icon {
    position: absolute;
    top: 50%;
    left: .75rem;
    transform: translateY(-50%);
    color: #82868b;
    pointer-events: none;
  }
}
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f0f0f0;
  .filter {
    flex: 0 1 280px;
  }
}
.orders-table ::v-deep {
  font-size: .8125rem;
  th {
    padding: .625rem .875rem;
    border-top: 0;
    border-bottom: 1px solid #e0e0e0;
    background: #f6f6f6 !important;
    font-size: .7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .5px;
    color: #6e6b7b;
    white-space: nowrap;
  }
  td {
    padding: .75rem .875rem;
    border-top: 0;
    border-bottom: 1px solid #f0f0f0;
    vertical-align: middle;
    color: #4d4d4d;
  }
  tbody tr:hover td {
    background: #fffaf5;
  }
  .column-actions {
    text-align: right;
  }
  .b-table-empty-row td {
    padding: 2rem;
    text-align: center;
    color: #82868b;
  }
}
.amount {
  font-weight: 700;
  color: #1e1e1e;
  white-space: nowrap;
}
.client-link {
  font-weight: 600;
  color: #ac1a19;
}
.doc-links {
  display: flex;
  flex-wrap: wrap;
  gap: .35rem;
}
.doc-link {
  display: inline-flex;
  align-items: center;
  gap: .25rem;
  padding: .2rem .55rem;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  font-size: .72rem;
  font-weight: 600;
  color: #4d4d4d;
  white-space: nowrap;
  &:hover {
    border-color: #ac1a19;
    color: #ac1a19;
  }
}
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: .75rem;
  padding: .75rem 1.25rem;
  border-top: 1px solid #f0f0f0;
  .per-page {
    width: 70px;
  }
}
</style>
